import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { NotificationPermissionService } from '../services/notificationPermissionService';
import SwipePreferencesModal from '../components/UI/SwipePreferencesModal';
import { NotificationStatus } from '../components/UI/NotificationStatus';

export const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { 
    settings, 
    loading: settingsLoading, 
    error: settingsError, 
    updateSetting, 
    updateSettings,
    exportData, 
    deleteAccount 
  } = useSettings();

  const [activeTab, setActiveTab] = useState('general');
  const [showSwipePreferences, setShowSwipePreferences] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Afficher une notification temporaire
  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSettingChange = async (key: string, value: boolean | string) => {
    const success = await updateSetting(key as any, value);
    if (success) {
      showNotification('Paramètre mis à jour !');
      
      // Actions spéciales pour certains paramètres
      if (key === 'notifications' && value === true) {
        const hasPermission = await NotificationPermissionService.requestPermission();
        if (hasPermission) {
          NotificationPermissionService.sendTestNotification();
        }
      }
      
      if (key === 'soundEnabled' && value === true) {
        NotificationPermissionService.playNotificationSound();
      }
    } else {
      showNotification('Erreur lors de la mise à jour');
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const data = await exportData();
      if (data) {
        // Créer un blob et télécharger le fichier
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `jupipotes-data-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showNotification('Données exportées avec succès !');
      } else {
        showNotification('Erreur lors de l\'export des données');
      }
    } catch (error) {
      showNotification('Erreur lors de l\'export des données');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const success = await deleteAccount();
      if (success) {
        showNotification('Compte supprimé avec succès');
        await logout();
        navigate('/');
      } else {
        showNotification('Erreur lors de la suppression du compte');
      }
    } catch (error) {
      showNotification('Erreur lors de la suppression du compte');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleEditProfile = () => {
    navigate('/profile');
  };

  const handleManageWallets = () => {
    // TODO: Implémenter la gestion des wallets
    showNotification('Fonctionnalité à venir...');
  };

  const handleChangePassword = () => {
    // TODO: Implémenter le changement de mot de passe
    showNotification('Fonctionnalité à venir...');
  };

  const handleTwoFactor = () => {
    // TODO: Implémenter l'authentification à deux facteurs
    showNotification('Fonctionnalité à venir...');
  };

  return (
    <div className="min-h-screen cosmic-bg p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="cosmic-card p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center animate-glow">
              <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-emerald-100 cosmic-text">Paramètres</h1>
              <p className="text-emerald-300/70">Configurez votre expérience Jupipotes</p>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="cosmic-card p-4 mb-6 bg-emerald-900/30 border-emerald-500/50">
            <div className="flex items-center space-x-2">
              <span className="text-emerald-400">✅</span>
              <span className="text-emerald-200">{notification}</span>
            </div>
          </div>
        )}

        {/* Loading state */}
        {settingsLoading && (
          <div className="cosmic-card p-6 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-200">Chargement des paramètres...</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {settingsError && (
          <div className="cosmic-card p-4 mb-6 bg-red-900/30 border-red-500/50">
            <div className="flex items-center space-x-2">
              <span className="text-red-400">❌</span>
              <span className="text-red-200">{settingsError}</span>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="cosmic-card p-2 mb-6">
          <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
            {[
              { id: 'general', label: 'Général', icon: '⚙️' },
              { id: 'notifications', label: 'Notifications', icon: '🔔' },
              { id: 'privacy', label: 'Confidentialité', icon: '🔒' },
              { id: 'trading', label: 'Trading', icon: '📈' },
              { id: 'account', label: 'Compte', icon: '👤' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'cosmic-button-primary'
                    : 'cosmic-button-secondary'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Général */}
          {activeTab === 'general' && (
            <div className="cosmic-card p-6">
              <h2 className="text-xl font-semibold text-emerald-100 mb-6 flex items-center">
                <span className="mr-2">⚙️</span>
                Préférences générales
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Mode sombre</h3>
                    <p className="text-sm text-emerald-400/70">Interface cosmique sombre (recommandé)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.darkMode}
                      onChange={(e) => handleSettingChange('darkMode', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-cyan-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Sons activés</h3>
                    <p className="text-sm text-emerald-400/70">Sons d'interface et notifications</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.soundEnabled}
                      onChange={(e) => handleSettingChange('soundEnabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-cyan-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Connexion automatique</h3>
                    <p className="text-sm text-emerald-400/70">Se connecter automatiquement au wallet</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoConnect}
                      onChange={(e) => handleSettingChange('autoConnect', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-cyan-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Raccourcis de glissement</h3>
                    <p className="text-sm text-emerald-400/70">Configurez les gestes de swipe sur les posts pour mobile</p>
                  </div>
                  <button
                    onClick={() => setShowSwipePreferences(true)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    Configurer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="cosmic-card p-6">
              <h2 className="text-xl font-semibold text-emerald-100 mb-6 flex items-center">
                <span className="mr-2">🔔</span>
                Notifications
              </h2>
              
              <div className="space-y-6">
                <NotificationStatus 
                  isEnabled={settings.notifications}
                  onToggle={(enabled) => handleSettingChange('notifications', enabled)}
                />

                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Notifications email</h3>
                    <p className="text-sm text-emerald-400/70">Recevoir des emails pour les événements importants</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-cyan-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Marquer automatiquement comme lu</h3>
                    <p className="text-sm text-emerald-400/70">Marquer les messages comme lus automatiquement</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoMarkAsRead}
                      onChange={(e) => handleSettingChange('autoMarkAsRead', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-cyan-500"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Confidentialité */}
          {activeTab === 'privacy' && (
            <div className="cosmic-card p-6">
              <h2 className="text-xl font-semibold text-emerald-100 mb-6 flex items-center">
                <span className="mr-2">🔒</span>
                Confidentialité et sécurité
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div>
                    <h3 className="font-medium text-emerald-200">Afficher le statut en ligne</h3>
                    <p className="text-sm text-emerald-400/70">Permettre aux autres de voir votre statut en ligne</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showOnlineStatus}
                      onChange={(e) => handleSettingChange('showOnlineStatus', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-cyan-500"></div>
                  </label>
                </div>

                <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <h3 className="font-medium text-emerald-200 mb-2">Données personnelles</h3>
                  <p className="text-sm text-emerald-400/70 mb-4">Gérez vos données personnelles et votre historique</p>
                  <div className="space-y-2">
                    <button 
                      onClick={handleExportData}
                      disabled={isExporting}
                      className="cosmic-button-secondary text-sm disabled:opacity-50"
                    >
                      {isExporting ? '📥 Export en cours...' : '📥 Exporter mes données'}
                    </button>
                    <button 
                      onClick={() => setShowDeleteConfirm(true)}
                      className="cosmic-button text-red-400 hover:text-red-300 text-sm"
                    >
                      🗑️ Supprimer mon compte
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trading */}
          {activeTab === 'trading' && (
            <div className="cosmic-card p-6">
              <h2 className="text-xl font-semibold text-emerald-100 mb-6 flex items-center">
                <span className="mr-2">📈</span>
                Paramètres de trading
              </h2>
              
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <h3 className="font-medium text-emerald-200 mb-2">Tolérance de slippage</h3>
                  <p className="text-sm text-emerald-400/70 mb-4">Tolérance de prix pour les échanges automatiques</p>
                  <select
                    value={settings.slippageTolerance}
                    onChange={(e) => handleSettingChange('slippageTolerance', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-emerald-800/30 rounded-lg text-emerald-100 focus:ring-2 focus:ring-cyan-400/70 focus:border-transparent"
                  >
                    <option value="0.1">0.1% - Très strict</option>
                    <option value="0.5">0.5% - Strict</option>
                    <option value="1.0">1.0% - Standard</option>
                    <option value="2.0">2.0% - Flexible</option>
                    <option value="3.0">3.0% - Très flexible</option>
                  </select>
                </div>

                <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <h3 className="font-medium text-emerald-200 mb-2">Frais de transaction</h3>
                  <p className="text-sm text-emerald-400/70 mb-4">Vitesse de confirmation préférée</p>
                  <div className="space-y-2">
                    {[
                      { value: 'slow', label: 'Lent (économique)', desc: '~30 sec' },
                      { value: 'normal', label: 'Normal', desc: '~15 sec' },
                      { value: 'fast', label: 'Rapide', desc: '~5 sec' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-emerald-800/20">
                        <input
                          type="radio"
                          name="transactionSpeed"
                          value={option.value}
                          checked={settings.transactionSpeed === option.value}
                          onChange={(e) => handleSettingChange('transactionSpeed', e.target.value)}
                          className="text-cyan-400 focus:ring-cyan-400"
                        />
                        <div>
                          <span className="text-emerald-200">{option.label}</span>
                          <span className="text-emerald-400/70 text-sm ml-2">{option.desc}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Compte */}
          {activeTab === 'account' && (
            <div className="cosmic-card p-6">
              <h2 className="text-xl font-semibold text-emerald-100 mb-6 flex items-center">
                <span className="mr-2">👤</span>
                Informations du compte
              </h2>
              
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center text-2xl">
                      {user?.displayName?.charAt(0) || '🚀'}
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-emerald-200">{user?.displayName || 'Jupipote Anonyme'}</h3>
                      <p className="text-emerald-400/70">{user?.email || 'Non connecté'}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleEditProfile}
                    className="cosmic-button-secondary"
                  >
                    ✏️ Modifier le profil
                  </button>
                </div>

                <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <h3 className="font-medium text-emerald-200 mb-2">Wallet connecté</h3>
                  <p className="text-sm text-emerald-400/70 mb-2">
                    {user?.walletAddress ? 
                      `${user.walletAddress.substring(0, 8)}...${user.walletAddress.substring(user.walletAddress.length - 6)}` 
                      : 'Aucun wallet connecté'
                    }
                  </p>
                  <button 
                    onClick={handleManageWallets}
                    className="cosmic-button-secondary text-sm"
                  >
                    🔗 Gérer les wallets
                  </button>
                </div>

                <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
                  <h3 className="font-medium text-emerald-200 mb-2">Sécurité</h3>
                  <div className="space-y-2">
                    <button 
                      onClick={handleChangePassword}
                      className="cosmic-button-secondary text-sm w-full"
                    >
                      🔑 Changer le mot de passe
                    </button>
                    <button 
                      onClick={handleTwoFactor}
                      className="cosmic-button-secondary text-sm w-full"
                    >
                      📱 Authentification à deux facteurs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="cosmic-card p-4 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-emerald-400/70 text-sm">Les paramètres sont sauvegardés automatiquement</p>
            <button 
              onClick={() => showNotification('Paramètres sauvegardés !')}
              className="cosmic-button-primary"
            >
              ✅ Tout sauvegarder
            </button>
          </div>
        </div>
      </div>

      {/* Modal de préférences de swipe */}
      <SwipePreferencesModal
        isOpen={showSwipePreferences}
        onClose={() => setShowSwipePreferences(false)}
      />

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cosmic-card p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-red-400 mb-4">⚠️ Supprimer le compte</h3>
            <p className="text-emerald-200 mb-6">
              Cette action est irréversible. Toutes vos données seront définitivement supprimées.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="cosmic-button-secondary flex-1"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="cosmic-button bg-red-600 hover:bg-red-700 text-white flex-1 disabled:opacity-50"
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
